// ============================================================
// Nova Sigma — Spielformeln (Single Source of Truth)
// ============================================================

import { RESOURCE_BASE, BUILDINGS, RESEARCH, SHIPS, GAME_CONFIG } from './game-config.js';

// ---- Ressourcen-Produktion ---------------------------------

/**
 * Berechnet die Rohproduktion pro Tick für eine Mine.
 */
export function calcMineProduction(type: 'metal' | 'crystal' | 'deuterium', level: number): number {
  if (level === 0) return 0;
  const cfg = RESOURCE_BASE[type];
  const exponent = type === 'deuterium' ? level - 1 : level;
  return cfg.base + cfg.rate * level * Math.pow(cfg.multiplier, exponent);
}

/**
 * Energie-Produktion des Solarkraftwerks pro Tick.
 */
export function calcSolarPlantEnergy(level: number): number {
  if (level === 0) return 0;
  return Math.floor(20 * level * Math.pow(1.1, level));
}

/**
 * Energie-Produktion des Fusionskraftwerks pro Tick.
 */
export function calcFusionPlantEnergy(level: number, energyTechLevel: number): number {
  if (level === 0) return 0;
  return Math.floor(30 * level * Math.pow(1.05 + energyTechLevel * 0.01, level));
}

/**
 * Deuterium-Verbrauch des Fusionskraftwerks pro Tick.
 */
export function calcFusionPlantFuelConsumption(level: number): number {
  if (level === 0) return 0;
  return Math.floor(10 * level * Math.pow(1.1, level));
}

/**
 * Energie-Verbrauch einer Mine pro Tick.
 */
export function calcMineEnergyConsumption(type: 'metal' | 'crystal' | 'deuterium', level: number): number {
  if (level === 0) return 0;
  const base = type === 'metal' ? 10 : type === 'crystal' ? 10 : 20;
  return Math.ceil(base * level * Math.pow(1.1, level));
}

/**
 * Effektive Produktion unter Berücksichtigung Energie-Malus und Forschungsbonus.
 */
export function calcEffectiveProduction(
  rawProduction: number,
  availableEnergy: number,
  requiredEnergy: number,
  miningTechLevel: number,
): number {
  const energyFactor = requiredEnergy > 0
    ? Math.min(1, availableEnergy / requiredEnergy)
    : 1;
  const researchBonus = 1 + miningTechLevel * 0.05;
  return rawProduction * energyFactor * researchBonus;
}

// ---- Speicherkapazität ------------------------------------

export function calcStorage(level: number): number {
  return Math.floor(50000 * Math.floor(2.5 * Math.exp(20 * level / 33)));
}

// ---- Baukosten & -zeit ------------------------------------

export function calcBuildingCost(buildingType: string, targetLevel: number): { metal: number; crystal: number; deuterium: number } {
  const def = BUILDINGS[buildingType];
  if (!def) throw new Error(`Unknown building: ${buildingType}`);
  const mult = Math.pow(def.cost_multiplier, targetLevel - 1);
  return {
    metal:     Math.floor(def.base_metal     * mult * 2),
    crystal:   Math.floor(def.base_crystal   * mult * 2),
    deuterium: Math.floor(def.base_deuterium * mult * 2),
  };
}

export function calcBuildTime(
  metalCost: number,
  crystalCost: number,
  robotFactoryLevel: number,
  naniteFactoryLevel: number,
): number {
  const raw = (metalCost + crystalCost)
    / (GAME_CONFIG.UNIVERSE_SPEED * 200 * (1 + robotFactoryLevel))
    * Math.pow(0.5, naniteFactoryLevel)
    * 3600;
  return Math.max(60, Math.ceil(raw));
}

// ---- Forschungskosten & -zeit -----------------------------

export function calcResearchCost(researchType: string, targetLevel: number): { metal: number; crystal: number; deuterium: number } {
  const def = RESEARCH[researchType];
  if (!def) throw new Error(`Unknown research: ${researchType}`);
  const mult = Math.pow(def.cost_multiplier, targetLevel - 1);
  return {
    metal:     Math.floor(def.base_metal     * mult * 2),
    crystal:   Math.floor(def.base_crystal   * mult * 2),
    deuterium: Math.floor(def.base_deuterium * mult * 2),
  };
}

export function calcResearchTime(
  metalCost: number,
  crystalCost: number,
  labLevel: number,
): number {
  const raw = (metalCost + crystalCost) / (200 * (1 + labLevel)) * 3600;
  return Math.max(120, Math.ceil(raw));
}

// ---- Schiffskosten ----------------------------------------

export function calcShipCost(shipType: string): { metal: number; crystal: number; deuterium: number } {
  const def = SHIPS[shipType];
  if (!def) throw new Error(`Unknown ship: ${shipType}`);
  return {
    metal:     def.base_metal,
    crystal:   def.base_crystal,
    deuterium: def.base_deuterium,
  };
}

export function calcShipBuildTime(
  shipType: string,
  shipyardLevel: number,
  naniteFactoryLevel: number,
): number {
  const cost = calcShipCost(shipType);
  return calcBuildTime(cost.metal, cost.crystal, shipyardLevel, naniteFactoryLevel);
}

// ---- Schiffsgeschwindigkeit --------------------------------

export function calcShipSpeed(
  shipType: string,
  combustionDriveLevel: number,
  impulseDriveLevel: number,
  hyperspaceDriveLevel: number,
): number {
  const def = SHIPS[shipType];
  if (!def) throw new Error(`Unknown ship: ${shipType}`);
  let bonus = 0;
  if (def.drive === 'combustion') bonus = combustionDriveLevel * 0.10;
  else if (def.drive === 'impulse') bonus = impulseDriveLevel * 0.20;
  else if (def.drive === 'hyperspace') bonus = hyperspaceDriveLevel * 0.30;
  return Math.floor(def.base_speed * (1 + bonus));
}

// ---- Flugzeit & Treibstoff --------------------------------

export function calcDistance(
  g1: number, s1: number,
  g2: number, s2: number,
): number {
  if (g1 !== g2) return Math.abs(g1 - g2) * 20000;
  return Math.abs(s1 - s2) * 5 + 5;
}

export function calcFlightTime(
  distance: number,
  fleetSpeed: number,
  universeSpeed = GAME_CONFIG.UNIVERSE_SPEED,
): number {
  return Math.round(3500 / universeSpeed * Math.sqrt(distance * 10 / fleetSpeed) + 10);
}

export function calcFuelConsumption(
  shipType: string,
  count: number,
  distance: number,
  speedPercent: number,
): number {
  const def = SHIPS[shipType];
  if (!def) return 0;
  const factor = ((speedPercent / 100) + 1) ** 2;
  return Math.ceil(def.fuel_consumption * count * distance / 7000 * factor);
}

// ---- Punkte -----------------------------------------------

export function calcPoints(
  metalSpent: number,
  crystalSpent: number,
  deuteriumSpent: number,
): number {
  return Math.floor((metalSpent + crystalSpent + deuteriumSpent) / 1000);
}

// ---- Kolonien ---------------------------------------------

export function calcMaxColonies(astrophysicsLevel: number): number {
  return Math.floor((astrophysicsLevel + 1) / 2);
}

// ---- Planetenfelder / Temperatur --------------------------

export function calcPlanetFields(position: number): number {
  const ranges: [number, number, number, number][] = [
    // [posMin, posMax, fieldsMin, fieldsMax]
    [1,  3,  40, 110],
    [4,  6, 120, 250],
    [7,  9, 105, 220],
    [10, 12,  75, 170],
    [13, 15,  50, 130],
  ];
  for (const [min, max, fMin, fMax] of ranges) {
    if (position >= min && position <= max) {
      return fMin + Math.floor(Math.random() * (fMax - fMin + 1));
    }
  }
  return 100;
}

export function calcPlanetTemperature(position: number): number {
  const ranges: [number, number, number, number][] = [
    [1,  3,  50, 240],
    [4,  6, -20,  50],
    [7,  9, -60, -20],
    [10, 12, -120, -60],
    [13, 15, -220, -120],
  ];
  for (const [min, max, tMin, tMax] of ranges) {
    if (position >= min && position <= max) {
      return tMin + Math.floor(Math.random() * (tMax - tMin + 1));
    }
  }
  return 0;
}
